/* CRM Sun Is Up : pipeline d'onboarding des membres, communications et paramètres.
   Le simulateur est dans components/simulator/SimulatorModal.tsx. */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'react-hot-toast';
import { Archive, LayoutGrid, List, Loader2, Mail, Plus, RefreshCw, Search, Settings2, Users, X } from 'lucide-react';
import type { AccountState, CrmContact, Directory, Role, SharingOperation, Stage } from './types';
import { ROLE_LABELS, STAGE_INDEX, displayName, isGroupCard } from './types';
import { formatErrors, missingFields, setRequirementOverrides } from './validation';
import { fetchAccountStatuses, fetchContacts, fetchDirectory, fetchFieldRequirements, fetchOperations } from './api';
import { NewLeadDialog } from './NewLeadDialog';
import { TodoTab } from './TodoTab';
import { PipelineBoard } from './PipelineBoard';
import { ContactListView } from './ContactListView';
import { ContactDrawer } from './ContactDrawer';
import { GroupDrawer } from './GroupDrawer';
import { type GroupStats, fetchGroupStats } from './participants';
import { TransitionDialog } from './TransitionDialog';
import { CommunicationsTab } from './CommunicationsTab';
import { SettingsTab } from './SettingsTab';
import { errorMessage } from './ui';
import { CRM_ADMIN_EMAIL, SESSION_EXPIRED_MESSAGE, crmSessionProblem } from './session';
import { supabase } from '../../lib/supabase';

type Tab = 'pipeline' | 'list' | 'communications' | 'settings';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  operationId?: string | null;
  /** Conservé pour compatibilité avec SharingOperationsPage. */
  isSuperAdmin?: boolean;
}

type DrawerState = { mode: 'create' } | { mode: 'edit'; contactId: string; highlight?: Stage | null } | null;

/** '' = toutes les opérations, 'none' = sans opération, sinon l'id de l'opération. */
type OperationFilter = string;
const OP_FILTER_KEY = 'crm-operation-filter';
const readOpFilter = (): OperationFilter => {
  try { return localStorage.getItem(OP_FILTER_KEY) ?? ''; } catch { return ''; }
};
const EMPTY_DIRECTORY: Directory = { groups: [], companyNames: [], companyNumberToName: {} };

export function CRMModal({ isOpen, onClose, operationId }: Props) {
  const [tab, setTab] = useState<Tab>('pipeline');
  const [contacts, setContacts] = useState<CrmContact[]>([]);
  const [accounts, setAccounts] = useState<Record<string, AccountState>>({});
  const [directory, setDirectory] = useState<Directory>(EMPTY_DIRECTORY);
  const [groupStats, setGroupStats] = useState<Record<string, GroupStats>>({});
  const refreshGroupStats = () => { fetchGroupStats().then(setGroupStats).catch(() => {}); };
  const [opFilter, setOpFilter] = useState<OperationFilter>(readOpFilter);
  const [operations, setOperations] = useState<SharingOperation[]>([]);
  const [loading, setLoading] = useState(false);

  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState<Role | 'all'>('all');
  const [showArchived, setShowArchived] = useState(false);

  const [drawer, setDrawer] = useState<DrawerState>(null);
  const [newLead, setNewLead] = useState(false);
  /** Onglet Communications : todolist du mois ou envoi groupé. */
  const [commView, setCommView] = useState<'todo' | 'send'>('todo');
  /** Réglages des champs obligatoires chargés (ils sont appliqués avant l'affichage des contacts). */
  const [, setRequirementsVersion] = useState(0);
  const [transition, setTransition] = useState<{ contact: CrmContact; target: Stage } | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      // Champs obligatoires par étape (CRM → Paramètres) ; sans réglage enregistré : valeurs par défaut.
      setRequirementOverrides(await fetchFieldRequirements().catch(() => ({})));
      const [list, ops] = await Promise.all([fetchContacts(showArchived), fetchOperations()]);
      setContacts(list);
      setOperations(ops);
      setAccounts(await fetchAccountStatuses(list).catch(() => ({})));
      fetchDirectory().then(setDirectory).catch(() => setDirectory(EMPTY_DIRECTORY));
      fetchGroupStats().then(setGroupStats).catch(() => setGroupStats({}));
    } catch (err) {
      toast.error(`Chargement du CRM impossible : ${errorMessage(err)}`);
    } finally {
      setLoading(false);
    }
  }, [showArchived]);

  useEffect(() => {
    if (isOpen) load();
  }, [isOpen, load]);

  /* Session : sans le compte info@sunisup.be, la base ignore les modifications. Le CRM le signale aussitôt
     (déconnexion automatique, déconnexion depuis un autre onglet, jeton non renouvelé). */
  const [sessionProblem, setSessionProblem] = useState<string | null>(null);
  useEffect(() => {
    if (!isOpen) return;
    let active = true;
    const verify = () => { crmSessionProblem().then(p => { if (active) setSessionProblem(p); }).catch(() => {}); };
    verify();
    const { data } = supabase.auth.onAuthStateChange((event: string, session: { user?: { email?: string } } | null) => {
      if (!active) return;
      if (event === 'SIGNED_OUT' || !session) verify();
      else if ((session.user?.email ?? '').toLowerCase() === CRM_ADMIN_EMAIL) setSessionProblem(null);
    });
    const onVisible = () => { if (!document.hidden) verify(); };
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      active = false;
      data?.subscription?.unsubscribe();
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [isOpen]);

  const upsertLocal = (c: CrmContact) => {
    setContacts(prev => {
      if (c.archived_at && !showArchived) return prev.filter(p => p.id !== c.id);
      return prev.some(p => p.id === c.id) ? prev.map(p => (p.id === c.id ? c : p)) : [c, ...prev];
    });
  };

  /** Changement d'étape demandé (glisser-déposer ou bouton de la fiche). */
  const requestMove = (c: CrmContact, target: Stage) => {
    if (c.stage === target) return;
    if (c.archived_at) { toast.error('Restaurez le contact avant de le déplacer'); return; }
    if (STAGE_INDEX[target] > STAGE_INDEX[c.stage]) {
      const missing = missingFields(c, target, { excludeDialogFields: true });
      const invalid = Object.keys(formatErrors(c)).length > 0;
      if (missing.length > 0 || invalid) {
        toast.error(missing.length > 0
          ? `${displayName(c)} : ${missing.length} champ${missing.length > 1 ? 's' : ''} à compléter avant « ${target === 'contract_sent' ? 'Contrat envoyé' : 'cette étape'} »`
          : `${displayName(c)} : corrigez les champs invalides`);
        setDrawer({ mode: 'edit', contactId: c.id, highlight: target });
        return;
      }
    }
    setDrawer(null);
    setTransition({ contact: c, target });
  };

  const changeOpFilter = (value: OperationFilter) => {
    setOpFilter(value);
    try { localStorage.setItem(OP_FILTER_KEY, value); } catch { /* préférence non mémorisée */ }
  };

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return contacts.filter(c => {
      if (roleFilter !== 'all') {
        // Un groupe peut mélanger les rôles : il apparaît dès qu'un de ses EAN a ce rôle.
        const stats = isGroupCard(c) && c.groupe ? groupStats[c.groupe.trim().toLowerCase()] : undefined;
        if (stats ? !(stats.roles[roleFilter] ?? 0) : c.role !== roleFilter) return false;
      }
      // Les contacts sans opération (leads, contrats en cours) restent visibles avec chaque filtre.
      if (opFilter === 'none' && c.sharing_operation_id) return false;
      if (opFilter && opFilter !== 'none' && c.sharing_operation_id && c.sharing_operation_id !== opFilter) return false;
      if (!q) return true;
      return [displayName(c), c.email, c.company_number, c.phone, c.groupe, ...c.meters.flatMap(m => [m.ean_code, m.name])]
        .some(v => (v ?? '').toLowerCase().includes(q));
    });
  }, [contacts, search, roleFilter, opFilter, groupStats]);

  const drawerContact = drawer?.mode === 'edit' ? contacts.find(c => c.id === drawer.contactId) ?? null : null;
  const showFilters = tab === 'pipeline' || tab === 'list';

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex">
      <div className="bg-white w-full h-full flex flex-col overflow-hidden">
        {/* En-tête */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-neutral-100 shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 bg-[#F8BD3D] rounded-lg flex items-center justify-center">
              <Users className="w-5 h-5 text-neutral-900" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-neutral-900">CRM</h2>
              <p className="text-xs text-neutral-500">Onboarding des membres et communications</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {showFilters && (
              <button onClick={() => setNewLead(true)} className="flex items-center gap-1.5 px-3 py-2 rounded-lg bg-[#F8BD3D] hover:bg-[#E5A935] text-xs font-semibold text-neutral-900">
                <Plus className="w-3.5 h-3.5" />Nouveau lead
              </button>
            )}
            <button onClick={onClose} className="p-2 hover:bg-neutral-100 rounded-lg" aria-label="Fermer">
              <X className="w-5 h-5 text-neutral-500" />
            </button>
          </div>
        </div>

        {/* Onglets et filtres */}
        <div className="flex items-center justify-between border-b border-neutral-100 shrink-0 pr-4 flex-wrap">
          <div className="flex">
            {([
              { key: 'pipeline' as Tab, label: 'Pipeline', icon: LayoutGrid },
              { key: 'list' as Tab, label: 'Liste', icon: List },
              { key: 'communications' as Tab, label: 'Communications', icon: Mail },
              { key: 'settings' as Tab, label: 'Paramètres', icon: Settings2 },
            ]).map(({ key, label, icon: Icon }) => (
              <button key={key} onClick={() => setTab(key)}
                className={`flex items-center gap-2 px-5 py-3 text-sm font-medium border-b-2 transition-colors ${tab === key ? 'border-[#F8BD3D] text-neutral-900' : 'border-transparent text-neutral-500 hover:text-neutral-700'}`}>
                <Icon className="w-4 h-4" />{label}
              </button>
            ))}
          </div>
          {showFilters && (
            <div className="flex items-center gap-2 py-2">
              <div className="relative">
                <Search className="w-3.5 h-3.5 text-neutral-400 absolute left-2.5 top-1/2 -translate-y-1/2" />
                <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Nom, e-mail, EAN, BCE…"
                  className="w-56 pl-7 pr-3 py-1.5 text-xs border border-neutral-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#F8BD3D]/50" />
              </div>
              <select
                value={opFilter}
                onChange={e => changeOpFilter(e.target.value)}
                aria-label="Filtrer par opération"
                className="text-[11px] py-1.5 pl-2 pr-7 border border-neutral-200 rounded-lg bg-white text-neutral-700 focus:outline-none focus:ring-2 focus:ring-[#F8BD3D]/50"
              >
                <option value="">Toutes les opérations</option>
                {operations.map(o => <option key={o.id} value={o.id}>{o.name}</option>)}
                <option value="none">Sans opération</option>
              </select>
              <div className="flex gap-0.5 bg-neutral-100 rounded-lg p-0.5">
                {(['all', 'consumer', 'producer', 'prosumer'] as const).map(r => (
                  <button key={r} onClick={() => setRoleFilter(r)}
                    className={`text-[11px] px-2.5 py-1 rounded-md font-medium ${roleFilter === r ? 'bg-white text-neutral-900 shadow-sm' : 'text-neutral-500'}`}>
                    {r === 'all' ? 'Tous' : ROLE_LABELS[r]}
                  </button>
                ))}
              </div>
              <button onClick={() => setShowArchived(v => !v)}
                className={`flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-[11px] border ${showArchived ? 'border-neutral-400 bg-neutral-100 text-neutral-800' : 'border-neutral-200 text-neutral-500'}`}>
                <Archive className="w-3.5 h-3.5" />Archivés
              </button>
              <button onClick={load} className="p-1.5 rounded-lg border border-neutral-200 text-neutral-500 hover:bg-neutral-50" aria-label="Actualiser">
                {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
              </button>
            </div>
          )}
        </div>

        {/* Contenu */}
        <div className="flex-1 overflow-hidden flex flex-col">
          {tab === 'pipeline' && (loading && contacts.length === 0
            ? <div className="flex-1 flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin text-neutral-300" /></div>
            : <PipelineBoard contacts={filtered} accounts={accounts} groupStats={groupStats} onOpen={c => setDrawer({ mode: 'edit', contactId: c.id })} onMove={requestMove} />)}
          {tab === 'list' && <ContactListView contacts={filtered} accounts={accounts} groupStats={groupStats} onOpen={c => setDrawer({ mode: 'edit', contactId: c.id })} />}
          {tab === 'communications' && (
            <>
              <div className="flex gap-1 px-6 pt-3 shrink-0">
                {([['todo', 'À faire'], ['send', 'Envoi groupé']] as const).map(([key, label]) => (
                  <button key={key} onClick={() => setCommView(key)}
                    className={`px-3 py-1.5 rounded-lg text-xs font-semibold ${commView === key ? 'bg-neutral-900 text-white' : 'text-neutral-500 hover:bg-neutral-100'}`}>
                    {label}
                  </button>
                ))}
              </div>
              {commView === 'todo'
                ? <TodoTab contacts={contacts} operations={operations} onOpenContact={id => setDrawer({ mode: 'edit', contactId: id })} onContactUpdated={upsertLocal} />
                : <CommunicationsTab operationId={operationId} />}
            </>
          )}
          {tab === 'settings' && <SettingsTab onRequirementsSaved={() => setRequirementsVersion(v => v + 1)} />}
        </div>
      </div>

      {drawer?.mode === 'edit' && drawerContact && isGroupCard(drawerContact) && (
        <GroupDrawer
          key={drawerContact.id}
          contact={drawerContact}
          operations={operations}
          account={accounts[drawerContact.id]}
          directory={directory}
          onClose={() => { setDrawer(null); refreshGroupStats(); }}
          onSaved={upsertLocal}
        />
      )}

      {drawer && (drawer.mode === 'create' || (drawerContact && !isGroupCard(drawerContact))) && (
        <ContactDrawer
          key={drawer.mode === 'create' ? 'new' : `${drawer.contactId}-${drawer.highlight ?? ''}`}
          contact={drawer.mode === 'create' ? null : drawerContact}
          operations={operations}
          account={drawerContact ? accounts[drawerContact.id] : undefined}
          directory={directory}
          highlightStage={drawer.mode === 'edit' ? drawer.highlight : null}
          onClose={() => setDrawer(null)}
          onSaved={c => {
            upsertLocal(c);
            if (drawer.mode === 'create') setDrawer({ mode: 'edit', contactId: c.id });
          }}
          onRequestMove={requestMove}
        />
      )}

      {newLead && (
        <NewLeadDialog
          contacts={contacts}
          operations={operations}
          onCancel={() => setNewLead(false)}
          onManual={() => { setNewLead(false); setDrawer({ mode: 'create' }); }}
          onDone={(c, openDrawer) => {
            upsertLocal(c);
            setNewLead(false);
            if (openDrawer) setDrawer({ mode: 'edit', contactId: c.id });
          }}
        />
      )}

      {transition && (
        <TransitionDialog
          contact={transition.contact}
          target={transition.target}
          operations={operations}
          directory={directory}
          onCancel={() => setTransition(null)}
          onDone={(c, others = []) => {
            [c, ...others].forEach(upsertLocal);
            setTransition(null);
            const actives = [c, ...others].filter(x => x.stage === 'active' && !x.archived_at);
            if (actives.length) fetchAccountStatuses(actives).then(a => setAccounts(prev => ({ ...prev, ...a }))).catch(() => {});
            if (c.stage === 'active') {
              fetchDirectory().then(setDirectory).catch(() => {});
              refreshGroupStats();
            }
          }}
        />
      )}
      {sessionProblem && (
        <div className="fixed inset-0 flex items-center justify-center p-4" style={{ zIndex: 100, backgroundColor: 'rgba(23, 23, 23, 0.45)' }} role="alertdialog" aria-modal="true" aria-labelledby="crm-session-title">
          <div className="bg-white rounded-2xl shadow-xl max-w-md w-full p-6 space-y-4">
            <h3 id="crm-session-title" className="text-base font-semibold text-neutral-900">Reconnexion nécessaire</h3>
            <p className="text-sm text-neutral-600">
              {sessionProblem === SESSION_EXPIRED_MESSAGE
                ? 'Vous avez été déconnecté : déconnexion automatique après inactivité, ou déconnexion depuis un autre onglet.'
                : sessionProblem}
            </p>
            <p className="text-xs text-neutral-500">
              Les fiches affichées ne peuvent plus être modifiées tant que vous n’êtes pas reconnecté avec {CRM_ADMIN_EMAIL}.
            </p>
            <div className="flex justify-end gap-2">
              <button onClick={onClose} className="px-4 py-2 rounded-lg border border-neutral-200 text-sm text-neutral-700 hover:bg-neutral-50">
                Fermer le CRM
              </button>
              <button onClick={() => { window.location.href = '/'; }}
                className="px-4 py-2 rounded-lg bg-[#F8BD3D] hover:bg-[#E5A935] text-sm font-semibold text-neutral-900">
                Se reconnecter
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
